import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import http from '../utils/http';
import { RootState } from './store';
import { ConceptInput } from './TaxonomyChangesSlice'
import { EntityInput } from './EntitySlice'
import { DocumentReference, DocumentStatusInterface, DocumentNotification, DocumentError } from './DocumentsSlice'


export interface AspectInput {
    aspectType: string;
    aspectValue: string;
    aspectId: string;
}

export interface TableBreakdownEntryInput {
    label: string;
    aspect: AspectInput;
    sublevels: TableBreakdownEntryInput[];
    expandOnRender: boolean | null;
}

export interface TableInput {
    sliceHierarchy: TableBreakdownEntryInput | null;
    columnHierarchy: TableBreakdownEntryInput | null;
    rowHierarchy: TableBreakdownEntryInput | null;
}
                                                               
export interface AdHocQueryInput {
    accounts: string[];
    entities: string[];
    years: number[];
    dimensions: string[];
    layout: TableInput;
}

export interface AdHocState {

    accounts: string[];
    entities: string[];
    years: number[];
    dimensions: string[];
    layout: TableInput | null;
    status: 'idle' | 'loading' | 'failed';
    documents: DocumentReference;
    documentStatus: DocumentStatusInterface;
    error: DocumentError | null;
}
export interface AdHocReportStarter {
    query: AdHocQueryInput | null;
    reportType: 'ADHOC';
    fileType: 'XHTML' | 'IXBRL' | 'PDF' | 'EXCEL' | 'XBRL' | 'UNKNOWN';
}

const initialState: AdHocState = {
    documents: { uri: "", content: "", mimeType: "", documentId: null },
    error: null,
    documentStatus: { isEnabled: false, message: "", progress: 0, status: "info" },
    accounts: [],
    entities: [],
    years: [],
    dimensions: [],
    layout: null,
    status: 'idle'
}

const convertToYear = function (value: string | number): number {
    return parseInt(value.toString());
}


export const generateAdHocReport = createAsyncThunk(
    "Documents/GetAdHocReport",
    async (inputData: AdHocReportStarter)
        : Promise<DocumentReference | null> => {
        try {
            console.log("making call", inputData);
            const result = await http<AdHocReportStarter, DocumentReference>({
                path: `/document/adHocReport`,
                method: 'post',
                body: inputData
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });


export const adHocSlice = createSlice({
    name: 'AdHoc',
    initialState,
    reducers: {
        updateDocumentNotification: (state: any, action: any) => {

            let newDocumentStatus: DocumentStatusInterface = state.documentStatus;
            newDocumentStatus.isEnabled = true;
            newDocumentStatus.message = action.payload.description;
            newDocumentStatus.progress = (action.payload.currentStep / action.payload.totalSteps) * 100;

            if (action.payload.currentStep < action.payload.totalSteps) {
                newDocumentStatus.status = "info";
            }
            else if (action.payload.currentStep == action.payload.totalSteps) {
                newDocumentStatus.status = "success";
            }
            else if (action.payload.currentStep > action.payload.totalSteps) {
                newDocumentStatus.status = "danger";
            }
        },
        setAccounts: (state: any, action: any) => {
            console.log("setting accounts", action.payload);
            state.accounts = action.payload;
        },
        setEntities: (state: any, action: any) => {
            console.log("setting entities", action.payload);
            state.entities = action.payload;
        },
        setYearsSelected: (state: any, action: any) => {
            console.log("setting years", action.payload);
            state.years = action.payload;
        },
        setDimensions: (state: any, action: any) => {
            console.log("setting dimensions", action.payload);
            state.dimensions = action.payload;
        },
        setLayout: (state: any, action: any) => {
            state.layout = action.payload;
        },
        resetError: (state: any, action: any) => {
            if (state) {
                state.error = null;
            }
        },
        resetState(state: any) {
            state = initialState;
        }
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(generateAdHocReport.pending, (state: any) => {
                state.status = 'loading';
            })
            .addCase(generateAdHocReport.fulfilled, (state: any, action: any) => {
                if (action.payload != null) {
                    state.documents = action.payload;
                }
                state.status = "idle";
            })

    }
})

export const { setAccounts, setEntities, setYearsSelected, setDimensions, setLayout } = adHocSlice.actions;

export const selectAdHocData = (state: RootState) => state.adHoc ?
    {
        accounts: state.adHoc.accounts,
        entities: state.adHoc.entities,
        years: state.adHoc.years,
        dimensions: state.adHoc.dimensions,
        layout: state.adHoc.layout

    } : null;

export const selectLoadingState = (state: RootState) => state.adHoc.status;


export default adHocSlice.reducer;